// URL-param hook for the notifications_v2 push-tap → in-app mark-read sync
// (PR-N1 / issue #30 A).
//
// Flow:
//   1. Coach taps a push on their device. The push payload (set by
//      Firebase::PushNotifications::Base#data_with_app_notification) carries
//      `app_notification_id` both as a top-level data field and appended to
//      the `deep_link` query string (e.g. /workouts/123?app_notification_id=42).
//   2. The wrapper navigates into the web app on that deep link.
//   3. This helper reads `app_notification_id` from the URL on every full page
//      load AND every Inertia client-side navigation, fires a PATCH to mark
//      the row read (so the bell badge drops in real time across every open
//      tab via BadgeChannel), then strips the param via history.replaceState
//      so a back-nav doesn't re-fire.
//
// Half B ("flyout tap → dismiss push on device") is a separate PR (PR-N2)
// and DOES NOT live here. Do not extend this file with dismiss logic.

import axios from "axios";
import { router } from "@inertiajs/react";

const PARAM_NAME = "app_notification_id";

const markAppNotificationRead = (id) =>
  axios.patch(`/api/v1/me/app_notifications/${id}/mark_as_read`).catch(() => {
    // Retention may have purged the row before the user tapped the push —
    // 404 is the expected graceful no-op and we swallow it silently. Any
    // other failure is also swallowed (this is a best-effort UX hook, not
    // load-bearing); the regular flyout mark-all-as-read still works.
  });

const stripParamFromUrl = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete(PARAM_NAME);
    const search = parsed.searchParams.toString();
    const cleaned = `${parsed.pathname}${search ? `?${search}` : ""}${parsed.hash}`;
    window.history.replaceState({}, "", cleaned);
  } catch {
    // window.location may be in a state we can't parse during boot — ignore.
  }
};

// Extract the app_notification_id from the current URL, marking it read and
// stripping the param on success. Exported for the Jest spec; not for runtime
// callers.
export const handleAppNotificationParamOnce = () => {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(PARAM_NAME);
  if (raw === null) return;

  // Numeric guard — AppNotification ids are bigints; anything else is junk
  // (or an attempted injection). Bail without firing the request.
  if (!/^\d+$/.test(raw)) {
    stripParamFromUrl(window.location.href);
    return;
  }

  markAppNotificationRead(raw);
  stripParamFromUrl(window.location.href);
};

// Hook for the Inertia app bootstrap. Call once after createInertiaApp. Reads
// the param on first paint AND on every Inertia navigation so a deep-link nav
// between SPA pages still fires the mark-read.
export const initAppNotificationUrlParamHook = () => {
  handleAppNotificationParamOnce();

  router.on("navigate", () => {
    handleAppNotificationParamOnce();
  });
};
